import React, { useEffect, useState } from "react";
import { X, Phone, Mail as MailIcon, MessageCircle } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/lib/content";
import { BrandWordmark } from "@/components/landing/BrandWordmark";

/**
 * Premium mobile side menu — editorial style:
 * - Large serif numbers (01-10) per section
 * - Display-font labels in champagne
 * - Staggered slide-in animation
 * - Active section indicator with animated bar
 * - Hexagonal decorative pattern
 * - Quick-contact footer
 */
const MobileSideMenu = () => {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(NAV_LINKS[0]?.id || "");

  // Track active section
  useEffect(() => {
    const handler = () => {
      const offset = 140;
      let current = NAV_LINKS[0]?.id || "";
      for (const link of NAV_LINKS) {
        const el = document.getElementById(link.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - offset <= 0) current = link.id;
      }
      setActiveId(current);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  // Current section index (1-based)
  const activeIdx = Math.max(
    NAV_LINKS.findIndex((l) => l.id === activeId),
    0
  );
  const progressPct = ((activeIdx + 1) / NAV_LINKS.length) * 100;

  return (
    <div data-testid="mobile-side-menu">
      {/* Floating trigger — vertical "MENÚ" text on left edge */}
      <button
        data-testid="mobile-side-menu-toggle"
        onClick={() => setOpen(true)}
        aria-label="Abrir menú"
        className={`fixed left-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-[#3D1565] to-[#1F0A35] text-white pl-2 pr-2.5 py-5 rounded-r-2xl shadow-[0_8px_32px_-4px_rgba(61,21,101,0.6)] border border-l-0 border-[#E6D5B8]/40 hover:from-[#4a1c79] hover:to-[#2a0f4a] transition-all duration-300 ${
          open ? "opacity-0 pointer-events-none -translate-x-6" : "opacity-100"
        }`}
      >
        {/* Hex icon */}
        <svg width="18" height="20" viewBox="0 0 18 20" aria-hidden="true">
          <path
            d="M9 1 L17 5.5 L17 14.5 L9 19 L1 14.5 L1 5.5 Z"
            fill="none"
            stroke="#E6D5B8"
            strokeWidth="1.4"
          />
          <circle cx="9" cy="10" r="1.5" fill="#E6D5B8" />
        </svg>
        <span
          className="text-[10px] tracking-luxury uppercase font-medium text-[#E6D5B8]"
          style={{ writingMode: "vertical-rl", letterSpacing: "0.3em" }}
        >
          Menú
        </span>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          data-testid="mobile-side-menu-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-[#0B0410]/85 backdrop-blur-md"
          style={{ animation: "fadeIn 0.3s ease-out" }}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <aside
        data-testid="mobile-side-menu-drawer"
        className={`fixed top-0 left-0 bottom-0 z-50 w-[86vw] max-w-[380px] overflow-hidden transform transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!open}
        style={{
          background:
            "linear-gradient(155deg, #1A0E2A 0%, #0E0518 45%, #0B0410 100%)",
        }}
      >
        {/* Decorative hex pattern */}
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          aria-hidden="true"
        >
          <svg width="100%" height="100%">
            <defs>
              <pattern
                id="msmHex"
                width="70"
                height="121"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M35 0 L70 20 L70 60 L35 80 L0 60 L0 20 Z"
                  fill="none"
                  stroke="#E6D5B8"
                  strokeWidth="0.6"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#msmHex)" />
          </svg>
        </div>

        {/* Champagne ambient glow */}
        <div
          className="absolute -top-32 -right-32 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(230,213,184,0.25) 0%, rgba(230,213,184,0) 70%)",
          }}
          aria-hidden="true"
        />

        {/* Content */}
        <div className="relative h-full flex flex-col">
          {/* Header */}
          <div className="px-7 pt-7 pb-5">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <img
                  src={BRAND.logoWhite}
                  alt=""
                  className="h-10 w-10 object-contain shrink-0"
                />
                <BrandWordmark
                  title="EMB Consulting Group"
                  subtitle="Financial Planning · Regular Savings"
                  containerClassName="inline-block leading-tight"
                  titleClassName="font-display text-[#F9F6F0] text-base tracking-tight"
                  subtitleClassName="text-[#A397B4] uppercase mt-0.5"
                  subtitleStyle={{
                    fontSize: "0.45rem",
                    lineHeight: "1",
                    letterSpacing: "0.03em",
                  }}
                />
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Cerrar menú"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/15 text-[#F9F6F0]/80 hover:text-[#E6D5B8] hover:border-[#E6D5B8]/60 transition-colors shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* Progress */}
            <div>
              <div className="flex items-center justify-between text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
                <span>
                  Sección {String(activeIdx + 1).padStart(2, "0")}
                </span>
                <span>de {String(NAV_LINKS.length).padStart(2, "0")}</span>
              </div>
              <div className="h-px bg-white/10 relative overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#3D1565] via-[#E6D5B8] to-[#FBEFD3] transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Navigation list */}
          <nav className="flex-1 overflow-y-auto px-3 pb-4">
            <ul className="flex flex-col">
              {NAV_LINKS.map((l, i) => {
                const isActive = activeId === l.id;
                return (
                  <li
                    key={l.id}
                    style={{
                      animation: open
                        ? `slideInLeft 0.5s cubic-bezier(0.22,1,0.36,1) ${
                            0.08 + i * 0.05
                          }s both`
                        : "none",
                    }}
                  >
                    <button
                      data-testid={`mobile-side-link-${l.id}`}
                      onClick={() => go(l.id)}
                      className={`group w-full flex items-center gap-4 px-4 py-3.5 my-0.5 rounded-md text-left transition-all duration-300 ${
                        isActive
                          ? "bg-gradient-to-r from-[#3D1565]/45 to-transparent"
                          : "hover:bg-white/[0.04]"
                      }`}
                    >
                      {/* Active indicator bar */}
                      <span
                        className={`block w-0.5 self-stretch rounded-full transition-all duration-300 ${
                          isActive ? "bg-[#E6D5B8]" : "bg-transparent"
                        }`}
                      />

                      {/* Number */}
                      <span
                        className={`font-display text-2xl leading-none w-8 transition-colors duration-300 ${
                          isActive
                            ? "text-[#E6D5B8]"
                            : "text-[#F9F6F0]/30 group-hover:text-[#A397B4]"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>

                      {/* Label */}
                      <span
                        className={`flex-1 font-display text-xl tracking-tight transition-colors duration-300 ${
                          isActive
                            ? "text-[#F9F6F0]"
                            : "text-[#F9F6F0]/75 group-hover:text-[#F9F6F0]"
                        }`}
                      >
                        {l.label}
                      </span>

                      {/* Hex marker (only on active) */}
                      {isActive && (
                        <svg
                          width="10"
                          height="11"
                          viewBox="0 0 10 11"
                          aria-hidden="true"
                          className="shrink-0"
                        >
                          <path
                            d="M5 0 L10 2.75 L10 8.25 L5 11 L0 8.25 L0 2.75 Z"
                            fill="#E6D5B8"
                          />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer — Quick contacts + CTA */}
          <div className="relative px-5 pb-5 pt-4 border-t border-white/10 bg-[#0B0410]/60">
            <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mb-3">
              Contacto directo
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <a
                href="tel:+529841859759"
                className="flex flex-col items-center gap-1.5 py-2.5 rounded-md border border-white/10 text-[#F9F6F0]/85 hover:text-[#E6D5B8] hover:border-[#E6D5B8]/40 transition-colors"
                aria-label="Llamar"
              >
                <Phone size={15} />
                <span className="text-[10px] tracking-wider uppercase">Llamar</span>
              </a>
              <a
                href="https://wa.me/529841859759"
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center gap-1.5 py-2.5 rounded-md border border-white/10 text-[#F9F6F0]/85 hover:text-[#E6D5B8] hover:border-[#E6D5B8]/40 transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle size={15} />
                <span className="text-[10px] tracking-wider uppercase">WhatsApp</span>
              </a>
              <a
                href="mailto:atencion@embconsultingroup.com.mx"
                className="flex flex-col items-center gap-1.5 py-2.5 rounded-md border border-white/10 text-[#F9F6F0]/85 hover:text-[#E6D5B8] hover:border-[#E6D5B8]/40 transition-colors"
                aria-label="Correo"
              >
                <MailIcon size={15} />
                <span className="text-[10px] tracking-wider uppercase">Mail</span>
              </a>
            </div>
            <button
              data-testid="mobile-side-cta"
              onClick={() => go("contacto")}
              className="w-full bg-gradient-to-r from-[#3D1565] to-[#5e23a3] hover:from-[#4a1c79] hover:to-[#6c2bb8] text-white text-sm tracking-wider uppercase py-3.5 rounded-sm transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(61,21,101,0.6)]"
            >
              Agendar asesoría →
            </button>
          </div>
        </div>
      </aside>

      {/* Inline keyframes (kept locally so we don't pollute global CSS) */}
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MobileSideMenu;
